import React, { useCallback, useEffect, useRef } from "react";

export const INITIAL_PREVIEW_SHADOW = { x: 0.25, y: 0.25, width: 0.35, height: 0.35 };
export const PANEL_PREVIEW_BOUNDS = { x: 20, y: 20, width: 270, height: 400 };
const MIN_SIZE = 0.06;
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const corners = [
  ["nw", "πάνω αριστερά", "top left", "nwse-resize"],
  ["ne", "πάνω δεξιά", "top right", "nesw-resize"],
  ["sw", "κάτω αριστερά", "bottom left", "nesw-resize"],
  ["se", "κάτω δεξιά", "bottom right", "nwse-resize"],
];

function adjustShadow(shadow, handle, dx, dy) {
  if (handle === "move") return { ...shadow,
    x: clamp(shadow.x + dx, 0, 1 - shadow.width),
    y: clamp(shadow.y + dy, 0, 1 - shadow.height) };
  let left = shadow.x, bottom = shadow.y;
  let right = left + shadow.width, top = bottom + shadow.height;
  if (handle.includes("w")) left = clamp(left + dx, 0, right - MIN_SIZE);
  if (handle.includes("e")) right = clamp(right + dx, left + MIN_SIZE, 1);
  if (handle.includes("s")) bottom = clamp(bottom + dy, 0, top - MIN_SIZE);
  if (handle.includes("n")) top = clamp(top + dy, bottom + MIN_SIZE, 1);
  return { x: left, y: bottom, width: right - left, height: top - bottom };
}

export default function PanelShadowOverlay({ shadow, onChange, disabled = false, greek = false }) {
  const overlay = useRef(null);
  const drag = useRef(null);
  const interactive = Boolean(onChange) && !disabled;
  const bounds = PANEL_PREVIEW_BOUNDS;
  const x = bounds.x + shadow.x * bounds.width;
  const y = bounds.y + (1 - shadow.y - shadow.height) * bounds.height;
  const width = shadow.width * bounds.width, height = shadow.height * bounds.height;
  const finish = useCallback(() => {
    const active = drag.current;
    drag.current = null;
    if (active && overlay.current?.hasPointerCapture?.(active.id)) overlay.current.releasePointerCapture(active.id);
  }, []);
  useEffect(() => {
    if (!interactive) finish();
    return finish;
  }, [interactive, finish]);

  const panelPoint = event => {
    const svg = overlay.current?.ownerSVGElement;
    const matrix = svg?.getScreenCTM();
    if (!matrix) return null;
    const point = svg.createSVGPoint();
    point.x = event.clientX; point.y = event.clientY;
    const local = point.matrixTransform(matrix.inverse());
    return { x: (local.x - bounds.x) / bounds.width, y: 1 - (local.y - bounds.y) / bounds.height };
  };
  const start = (event, handle) => {
    if (!interactive || event.button !== 0 || drag.current) return;
    const point = panelPoint(event);
    if (!point) return;
    event.preventDefault(); event.stopPropagation();
    event.currentTarget.focus();
    drag.current = { id: event.pointerId, handle, point, shadow: { ...shadow } };
    overlay.current.setPointerCapture(event.pointerId);
  };
  const move = event => {
    const active = drag.current;
    if (!interactive || !active || active.id !== event.pointerId) return;
    const point = panelPoint(event);
    if (!point) return;
    event.preventDefault();
    onChange(adjustShadow(active.shadow, active.handle, point.x - active.point.x, point.y - active.point.y));
  };
  const end = event => { if (drag.current?.id === event.pointerId) finish(); };
  const keyDown = (event, handle) => {
    if (!interactive) return;
    if (event.key === "Escape" && drag.current) {
      event.preventDefault(); event.stopPropagation();
      onChange(drag.current.shadow); finish(); return;
    }
    const step = event.shiftKey ? 0.05 : 0.01;
    const delta = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] }[event.key];
    if (!delta) return;
    event.preventDefault(); event.stopPropagation();
    onChange(adjustShadow(shadow, handle, ...delta));
  };
  const controls = handle => onChange ? {
    role: "button", tabIndex: interactive ? 0 : -1, "aria-disabled": disabled || undefined,
    "aria-description": greek ? "Χρησιμοποιήστε τα βέλη. Shift και βέλος για μεγαλύτερο βήμα." : "Use arrow keys. Hold Shift for a larger step.",
    onPointerDown: event => start(event, handle), onKeyDown: event => keyDown(event, handle),
  } : {};
  return <g ref={overlay} data-testid="topology-shadow-overlay" onPointerMove={move} onPointerUp={end} onPointerCancel={end} onLostPointerCapture={end}>
    <rect data-testid="topology-shadow" x={x} y={y} width={width} height={height}
      fill="#111" fillOpacity={0.64} stroke={onChange ? "#fff" : "#111"} strokeWidth={1.5}
      {...controls("move")} aria-label={onChange ? greek ? "Μετακίνηση σκιάς" : "Move shadow" : undefined}
      style={{ cursor: interactive ? "grab" : undefined, touchAction: "none" }} />
    {onChange && width > 0 && height > 0 && corners.map(([handle, el, en, cursor]) =>
      <g key={handle}>
        <circle cx={handle.includes("w") ? x : x + width} cy={handle.includes("n") ? y : y + height}
          r={12} fill="transparent" {...controls(handle)}
          aria-label={greek ? `Αλλαγή μεγέθους σκιάς: ${el}` : `Resize shadow: ${en}`}
          style={{ cursor: interactive ? cursor : undefined, touchAction: "none" }} />
        <circle cx={handle.includes("w") ? x : x + width} cy={handle.includes("n") ? y : y + height}
          r={4.5} fill="#fff" stroke="#16324f" strokeWidth={1.5} pointerEvents="none" />
      </g>)}
  </g>;
}
